import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import AddExperienceModal from '../Experience/AddExperienceModal';
import { useUserContext } from '../../contexts/UserContext';
import { getGithubEntries } from '../../utils/APIs';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import { formatDate } from '../../utils/converter';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { getAgGridSortStorageKey } from '../UI/AgGrid';
import { Card, Button, Input, AgTableCard } from '../UI';

const ManageGithubEntries = () => {
    const [rowData, setRowData] = useState([]);
    const [showAddExperiencesModal, setShowAddExperiencesModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [gridApi, setGridApi] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const { userData } = useUserContext();
    const { enums, content } = useContentfulContext();
    const navigator = useNavigate();

    useEffect(() => {
        if (userData.defaultGithub) {
            getActivityData();
        }
    }, [userData?.defaultGithub?._id]);

    const getActivityData = async () => {
        try {
            if (!userData?.defaultGithub) {
                toast.error("Please add one GitHub integration and make it default");
                return;
            }
            setLoading(true);
            const result = await getGithubEntries({
                token: userData?.token,
                organizationId: userData?.selectedOrganization?.org_token,
                github_integration_id: userData?.defaultGithub?._id,
            });

            setError("");
            setRowData(result?.data?.files || []);
        } catch (err) {
            setError(err?.message || content?.COMMON_MESSAGE.something_wrong_msg);
            toast.error(err?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("manage-github-entries-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [
                    { colId: 'lastUpdatedAt', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 40,
            columnLimits: [
                {
                    colId: "path",
                    minWidth: 280,
                },
                {
                    colId: "size",
                    minWidth: 120,
                },
                {
                    colId: "lastUpdatedBy",
                    minWidth: 180,
                },
                {
                    colId: "lastUpdatedAt",
                    minWidth: 180,
                },
            ],
        };
    }, []);

    const handleCloseModal = () => {
        setShowAddExperiencesModal(false);
    };

    const onFilterTextBoxChanged = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (gridApi) {
            gridApi.setQuickFilter(value);
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        if (gridApi) {
            gridApi.setQuickFilter('');
        }
    };

    const colDefs = [
        {
            headerName: "File Path",
            field: "path",
            cellRenderer: ({ data }) => {
                return (
                    <Link
                        className="font-medium text-primary hover:text-primary-hover hover:underline font-mono text-xs"
                        to={`/experience/github-entries/details?id=${encodeURIComponent(data.path)}`}
                    >
                        {data.path}
                    </Link>
                );
            },
            filter: true,
            suppressMovable: true
        },
        {
            headerName: "Size",
            field: "size",
            filter: true,
            cellRenderer: ({ data }) => (
                <span className="text-stride-muted font-mono text-xs">
                    {data?.size} KB
                </span>
            ),
            suppressMovable: true
        },
        {
            headerName: "Last Updated By",
            field: "lastUpdatedBy",
            filter: true,
            suppressMovable: true,
            cellRenderer: ({ value }) => (
                <span className="text-stride-body text-sm font-normal">
                    {value || '—'}
                </span>
            )
        },
        {
            headerName: "Last Updated At",
            field: "lastUpdatedAt",
            sortable: true,
            cellRenderer: ({ data }) => (
                <span className="text-stride-muted text-xs">
                    {formatDate(data?.lastUpdatedAt)}
                </span>
            ),
            suppressMovable: true
        },
    ];

    return (
        <>
            {error && <ErrorAlert timeout={3000}>{error}</ErrorAlert>}
            <main id="main" className="stride-page-shell flex flex-col space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <span className="stride-eyebrow">Experiences / Manage</span>
                        <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                            GitHub Entries
                        </h1>
                        <p className="text-sm text-stride-muted mt-1">
                            {rowData?.length || 0} total files tracked in repository
                        </p>
                    </div>
                </div>

                {/* Empty State */}
                {!userData.defaultGithub && (
                    <Card className="text-center p-12 max-w-lg mx-auto">
                        <p className="text-base text-stride-heading font-medium mb-4">
                            Please add at least one GitHub Integration to view GitHub Entries.
                        </p>
                        <Button
                            variant="primary"
                            onClick={() => navigator('/setup/add-github-integration')}
                        >
                            Create New GitHub Integration
                        </Button>
                    </Card>
                )}

                <AddExperienceModal
                    showAddExperiencesModal={showAddExperiencesModal}
                    handleCloseModal={handleCloseModal}
                />

                {/* Quartz AG-Grid Table inside Card with Search in Header */}
                {userData.defaultGithub && (
                    <AgTableCard
                        header={
                            <div className="w-full md:w-80">
                                <Input
                                    placeholder="Search by file path..."
                                    value={searchTerm}
                                    onChange={onFilterTextBoxChanged}
                                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                    clearable
                                    onClear={handleClearSearch}
                                />
                            </div>
                        }
                        tableId="manage-github-entries-table"
                        loading={loading}
                        rowData={rowData}
                        columnDefs={colDefs}
                        autoSizeStrategy={autoSizeStrategy}
                        onGridReady={onGridReady}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        pagination={true}
                        paginationPageSize={10}
                        paginationPageSizeSelector={enums?.PAGINATION_PAGE_SIZE_SELECTOR}
                    />
                )}
            </main>
        </>
    );
};

export default ManageGithubEntries;
